// pathOps.ts - Enhanced version that supports flexible directories

// Core path utilities
const pathOps = {
    getFileName: (path: string): string => {
        return path.split('/').pop() || '';
    },

    getParentPath: (path: string): string => {
        const segments = path.split('/');
        segments.pop();
        return segments.join('/');
    },

    joinPaths: (...paths: string[]): string => {
        return paths
            .join('/')
            .split('/')
            .filter(segment => segment && segment !== '.')
            .join('/');
    },

    normalize: (path: string): string => {
        return path.split('/')
            .filter(segment => segment && segment !== '.')
            .join('/');
    }
};

// Allow any directory path
const getDirectoryForFile = (filePath: string, fileType: 'frontend' | 'backend', projectName: string): string => {
    // Check if the filePath already includes a directory structure
    if (filePath.includes('/')) {
        return pathOps.getParentPath(filePath);
    }

    // If no directory was specified, use default directories
    if (fileType === 'frontend') {
        return `${projectName}/src/frontend/src`;
    } else {
        return `${projectName}/src/backend`;
    }
};

// These functions are for compatibility with existing code
export const joinPaths = (...paths: string[]): string => {
    return paths.filter(Boolean).join('/').replace(/\/+/g, '/');
};

export const getParentPath = (path: string): string => {
    const normalizedPath = path.split(/[\/\\]/).join('/');
    const parts = normalizedPath.split('/');
    parts.pop(); // Remove filename
    return parts.join('/');
};

export const getFileName = (path: string): string => {
    const normalizedPath = path.split(/[\/\\]/).join('/');
    return normalizedPath.split('/').pop() || '';
};

// More permissive file validation
const validateFilePath = (filePath: string, fileType: 'frontend' | 'backend', projectName: string): boolean => {
    const extension = filePath.split('.').pop()?.toLowerCase();
    if (!extension) return false;

    if (fileType === 'frontend') {
        return getAllowedExtensions('frontend').includes('.' + extension);
    }

    return getAllowedExtensions('backend').includes('.' + extension);
};

// Extended allowed extensions
const getAllowedExtensions = (type: 'frontend' | 'backend'): string[] => {
    if (type === 'backend') {
        return ['.mo', '.did', '.json', '.txt'];
    }

    return [
        '.js', '.jsx', '.ts', '.tsx', '.css',
        '.html', '.png', '.jpg', '.jpeg', '.gif', '.svg',
        '.json', '.did', '.ico', '.md', '.txt', '.wasm'
    ];
};

// Updated to support custom paths
const createFilePath = (filePath: string, fileType: 'frontend' | 'backend', projectName: string): string => {
    // If already a full path, use it directly
    if (filePath.includes('/')) {
        return pathOps.normalize(filePath);
    }

    // Otherwise, use default directory
    const fileName = getFileName(filePath);
    const targetDir = getDirectoryForFile(fileName, fileType, projectName);
    return joinPaths(targetDir, fileName);
};

export {
    pathOps,
    getDirectoryForFile,
    validateFilePath,
    getAllowedExtensions,
    createFilePath
};